import { setToken, getToken, removeToken } from '../utils/tokenUtils';
import api from './api';

// Token management
const TOKEN_KEY = 'nikao_auth_token';
const USER_KEY = 'nikao_user_data';

class AuthService {
  constructor() {
    this.token = getToken();
    this.user = this.getUser();
    this.setupAxiosInterceptors();
  }

  // Setup is handled by the centralized api service
  setupAxiosInterceptors() {
    // No need to setup interceptors - handled by api service
  }

  // Login user (username/password)
  async login(credentials) {
    try {
      const response = await api.post('/auth/login', credentials);
      
      if (response.data.success) {
        const { user, token } = response.data.data;
        this.setToken(token);
        this.setUser(user);
        return { success: true, user };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed. Please try again.' 
      };
    }
  }

  // Login with Entra ID token (Sign in with Microsoft)
  async loginWithToken(azureToken) {
    try {
      const response = await api.post('/auth/login/token', { token: azureToken });

      if (response.data.code === 200) {
        const { token, userID } = response.data;
        this.setToken(token);
        // Fetch full user so we have username, email, role, etc.
        const verifyResult = await this.verifyToken();
        if (verifyResult.success) {
          return { success: true, user: verifyResult.user };
        }
        this.setUser({ id: userID });
        return { success: true, user: { id: userID } };
      }
      return {
        success: false,
        message: response.data.message || 'Token login failed.'
      };
    } catch (error) {
      console.error('Token login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Sign in with Microsoft failed. Please try again.'
      };
    }
  }

  // Register user (admin only)
  async register(userData) {
    try {
      const response = await api.post('/auth/register', userData);
      
      if (response.data.success) {
        return { success: true, user: response.data.data.user };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Register error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed. Please try again.' 
      };
    }
  }

  // Logout user
  async logout() {
    try {
      if (this.token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.removeToken();
      this.removeUser();
    }
  }

  // Get user profile
  async getProfile() {
    try {
      const response = await api.get('/auth/profile');
      
      if (response.data.success) {
        const user = response.data.data.user;
        this.setUser(user);
        return { success: true, user };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Get profile error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to get user profile.' 
      };
    }
  }

  // Verify token
  async verifyToken() {
    try {
      const response = await api.get('/auth/verify-token');
      
      if (response.data.success) {
        const user = response.data.data.user;
        this.setUser(user);
        return { success: true, user };
      } else {
        this.logout();
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Token verification error:', error);
      this.logout();
      return { success: false, message: 'Token verification failed.' };
    }
  }

  // Token management methods
  setToken(token) {
    this.token = token;
    setToken(token);
  }

  getToken() {
    if (!this.token) {
      this.token = getToken();
    }
    return this.token;
  }

  removeToken() {
    this.token = null;
    removeToken();
  }

  // User data management methods
  setUser(user) {
    this.user = user;
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  getUser() {
    if (!this.user) {
      try {
        const userData = localStorage.getItem(USER_KEY);
        this.user = userData ? JSON.parse(userData) : null;
      } catch (error) {
        console.error('Error parsing user data:', error);
        this.user = null;
      }
    }
    return this.user;
  }

  removeUser() {
    this.user = null;
    localStorage.removeItem(USER_KEY);
  }

  // Authentication state methods
  isAuthenticated() {
    return !!this.getToken();
  }

  getCurrentUser() {
    return this.getUser();
  }

  getUserRole() {
    const user = this.getUser();
    return user?.role || null;
  }

  isAdmin() {
    return this.getUserRole() === 'admin';
  }

  isUser() {
    const role = this.getUserRole();
    return role === 'admin' || role === 'user';
  }

  // Check if user has specific permission
  hasPermission(permission) {
    const role = this.getUserRole();
    
    const permissions = {
      admin: ['read', 'write', 'delete', 'manage_users'],
      user: ['read', 'write']
    };

    return permissions[role]?.includes(permission) || false;
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;